import {IService, SystemComponentType, ScreenOrientation} from '@gongxigames/framework/core/types';
import {CoreDI, CoreSettings} from '@gongxigames/framework/core/configs';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {System} from '@gongxigames/framework/core/services/system/System';
import {Device} from '@gongxigames/framework/core/services/Device';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';
import {IEventManagerWithAsync} from '@gongxigames/framework/core/types';
import {CoreEventsMap} from '@gongxigames/framework/core/events';

@injectable()
export class ScreenManagerV2 implements IService {
    public static NAME: symbol = CoreDI.ScreenManagerV2;
    public type: SystemComponentType = 'service';

    @inject(CoreDI.System)
    private readonly _system!: System;

    @inject(CoreDI.IEventManagerWithAsync)
    private readonly _eventManager!: IEventManagerWithAsync<CoreEventsMap>;

    @inject(CoreDI.Device)
    private readonly _device!: Device;

    @inject(CoreDI.Settings)
    private readonly _settings!: CoreSettings;

    private _forceOrientation?: ScreenOrientation;
    private _width = 0;
    private _height = 0;

    public init(): void {
        this._forceOrientation = this.getForceOrientation();
        strictCoreEmitter.on('CORE.renderLoop.tick', this.update.bind(this) , {owner: this});

        // TODO: migrate to strict event emitter
        this._eventManager.on('General.Game.INITIALIZED', this.emitResizeEvents.bind(this, true), this);
        this._eventManager.on('General.ResourcesLoading.LOADED_META_DATA', this.emitResizeEvents.bind(this, true), this);
        this._eventManager.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', this.emitResizeEvents.bind(this, true), this, -1);
    }

    public destroy(): void {
        strictCoreEmitter.offAllByOwner(this);
    }

    public update(): void {
        const app = this._system.app;
        const rect = app.getRenderRect();
        const ratio = this._device.getPixelRatio();
        const canvasWidth = Math.floor(rect.width * ratio);
        const canvasHeight = Math.floor(rect.height * ratio);
        const rendererWidth = this._system.renderer.width;
        const rendererHeight = this._system.renderer.height;
        const isInitialUpdateRequired = !this._width && !this._height;

        if (canvasWidth === rendererWidth && canvasHeight === rendererHeight && !isInitialUpdateRequired) {
            return;
        }

        this._width = canvasWidth;
        this._height = canvasHeight;

        app.renderer.resize(canvasWidth, canvasHeight);
        this.emitResizeEvents(isInitialUpdateRequired);
    }

    private emitResizeEvents(forceUpdate: boolean) {
        strictCoreEmitter.emit('CORE.screen.resize', {
            width: this._width,
            height: this._height,
            orientation: this.getOrientation(this._width, this._height),
            forceUpdate
        });
    }

    public getForceOrientation(): ScreenOrientation | undefined {
        /* develblock:start */
        const forceOrientation = this._settings.get('orientation');

        if (forceOrientation === 'landscape' || forceOrientation === 'portrait') {
            return forceOrientation;
        }
        /* develblock:end */
    }

    private getOrientation(width: number, height: number) {
        if (this._forceOrientation) {
            return this._forceOrientation;
        }

        return width > height ? ScreenOrientation.LANDSCAPE : ScreenOrientation.PORTRAIT;
    }
}
